import { useMemo } from "react";
import { Grid, Card, CardContent, Typography, Box, Link as MuiLink } from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import WifiIcon from "@mui/icons-material/Wifi";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import PeopleIcon from "@mui/icons-material/People";
import SecurityIcon from "@mui/icons-material/Security";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { StatCard } from "../components/StatCard";
import { SortableTable } from "../components/SortableTable";
import { baseChartOptions } from "../components/chartBase";
import { useData, useDataCtx } from "../context/DataContext";
import { computeAlerts } from "../utils/alertUtils";

interface OverviewProps {
  onNavigate: (tab: string) => void;
}

export default function Overview({ onNavigate }: OverviewProps) {
  const { period, thresholds } = useDataCtx();
  const dashboardData = useData();
  const { trendData } = dashboardData;

  const totalFleetWan = useMemo(() => trendData.reduce((sum, day) => sum + day.totalWAN, 0), [trendData]);
  const peakDay = useMemo(
    () => trendData.reduce((peak, day) => (day.totalWAN > peak.totalWAN ? day : peak), trendData[0]),
    [trendData]
  );
  const avgUsers = useMemo(() => {
    const sum = trendData.reduce((acc, day) => acc + day.captiveUsers, 0);
    return sum / (trendData.length || 1);
  }, [trendData]);
  const avgAuthPerUser = useMemo(() => {
    const sum = trendData.reduce((acc, day) => acc + day.authPerUser, 0);
    return sum / (trendData.length || 1);
  }, [trendData]);

  const alertCounts = useMemo(() => {
    const alerts = computeAlerts(dashboardData.rawData, dashboardData.siteAggregates, thresholds);
    return {
      critical: alerts.filter(a => a.level === "critical").length,
      warning: alerts.filter(a => a.level === "warning").length,
    };
  }, [dashboardData.rawData, dashboardData.siteAggregates, thresholds]);

  const vesselCount = useMemo(() => new Set(dashboardData.rawData.map(r => r.site)).size, [dashboardData.rawData]);

  const unauthSummaryData = useMemo(() => trendData.map(day => ({
    date: day.label,
    unauthPct: day.totalWAN > 0 ? (day.unauthUsage / day.totalWAN) * 100 : 0,
    staticPct: day.totalWAN > 0 ? (day.staticUsage / day.totalWAN) * 100 : 0,
  })), [trendData]);

  const areaOptions = {
    ...baseChartOptions,
    chart: { ...baseChartOptions.chart, type: "area", height: 340 },
    xAxis: { categories: trendData.map(d => d.label) },
    yAxis: { title: { text: "GB" } },
    tooltip: { shared: true, valueSuffix: " GB", valueDecimals: 2 },
    plotOptions: { area: { stacking: "normal", marker: { enabled: false } } },
    legend: { enabled: true },
    series: [
      { name: "Auth (Captive)", data: trendData.map(d => d.authUsage), color: "#2f2282" },
      { name: "Static Devices", data: trendData.map(d => d.staticUsage), color: "#3dab97" },
      { name: "Unauthorized", data: trendData.map(d => d.unauthUsage), color: "#d9534f" },
    ],
  };

  const barOptions = {
    ...baseChartOptions,
    chart: { ...baseChartOptions.chart, type: "column", height: 180 },
    xAxis: { categories: trendData.map(d => d.label) },
    yAxis: { title: { text: "" } },
    tooltip: { valueSuffix: " users" },
    legend: { enabled: false },
    series: [{ name: "Users", data: trendData.map(d => d.captiveUsers), color: "#5e3f9e" }],
  };

  const unauthColumns = [
    { key: "date", header: "Date", cell: (row: any) => row.date },
    {
      key: "unauthPct", header: "UnAuth %", align: "right" as const,
      cell: (row: any) => (
        <Box component="span" sx={{ fontFamily: "monospace", color: row.unauthPct > 15 ? "error.main" : "text.secondary" }}>
          {row.unauthPct.toFixed(1)}%
        </Box>
      ),
    },
    {
      key: "staticPct", header: "Static %", align: "right" as const,
      cell: (row: any) => (
        <Box component="span" sx={{ fontFamily: "monospace", color: row.staticPct > 30 ? "warning.main" : "text.secondary" }}>
          {row.staticPct.toFixed(1)}%
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Box mb={2}>
        <Typography variant="subtitle1" fontWeight={600}>Overview</Typography>
        <Typography variant="body2" color="text.secondary">
          Fleet-wide internet consumption across {vesselCount || 0} vessels ({period})
        </Typography>
      </Box>

      <Grid container spacing={2} mb={2}>
        <Grid item xs={6} md={2}>
          <StatCard compact highlight title="Total Fleet WAN" value={`${Math.round(totalFleetWan).toLocaleString()} GB`} icon={<WifiIcon fontSize="small" />} description={`${trendData.length}-day cumulative`} />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatCard compact title="Peak Day Usage" value={`${Math.round(peakDay?.totalWAN ?? 0).toLocaleString()} GB`} icon={<ShowChartIcon fontSize="small" />} description={peakDay?.label} />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatCard compact title="Avg Captive Users" value={Math.round(avgUsers).toLocaleString()} icon={<PeopleIcon fontSize="small" />} description="Fleet-wide daily avg" />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatCard compact title="Avg Auth / User" value={`${avgAuthPerUser.toFixed(2)} GB`} icon={<SecurityIcon fontSize="small" />} description="Per captive user / day" />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatCard
            compact title="Critical Vessels" value={alertCounts.critical}
            icon={<ErrorOutlineIcon fontSize="small" color="error" />}
            accentColor="error.main"
            action={<MuiLink component="button" variant="caption" onClick={() => onNavigate("alerts")}>View alerts →</MuiLink>}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatCard
            compact title="Warning Vessels" value={alertCounts.warning}
            icon={<WarningAmberIcon fontSize="small" color="warning" />}
            accentColor="warning.main"
            action={<MuiLink component="button" variant="caption" onClick={() => onNavigate("alerts")}>View alerts →</MuiLink>}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600}>Fleet WAN Usage Breakdown</Typography>
              <Typography variant="caption" color="text.secondary">Daily consumption by traffic category</Typography>
              <HighchartsReact highcharts={Highcharts} options={areaOptions} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600}>Captive Users</Typography>
              <Typography variant="caption" color="text.secondary">Daily fleet-wide connected users</Typography>
              <HighchartsReact highcharts={Highcharts} options={barOptions} />
              <Box mt={1}>
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  UNAUTH &amp; STATIC TRAFFIC MONITOR
                </Typography>
                <SortableTable data={unauthSummaryData} columns={unauthColumns} maxHeight={220} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
